import { Link, router } from '@inertiajs/react'
import { AlertCircle, Shield, ShieldAlert, ShieldCheck } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { BackofficeLicense } from '@/types'

type Props = {
  licenses: BackofficeLicense[]
  search: string
  status_filter: string
  pagination: PaginationMeta
}


export default function LicensesIndex({ licenses, search, status_filter, pagination }: Props) {
  return (
    <BackofficeLayout title="Licenses" breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Licenses' }]}>
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">Licenses</h1>
        <p className="text-sm text-muted-foreground">{pagination.total} {pagination.total === 1 ? 'license' : 'licenses'}</p>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <SearchInput value={search} placeholder="Search licenses..." />
        <select
          value={status_filter}
          onChange={e => router.get('/backoffice/licenses', { search, status: e.target.value }, { preserveState: true })}
          className="h-9 text-sm border rounded-md px-3"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="cancelled">Cancelled</option>
          <option value="issued">Issued</option>
          <option value="expired">Expired</option>
          <option value="custom">Custom</option>
        </select>
      </div>

      <div className="mt-4">
        {licenses.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <h3 className="text-sm font-medium text-foreground mb-1">No licenses found</h3>
          </div>
        ) : (
          <div className="border rounded-lg">
            {licenses.map((l, i) => {
              const isOverdue = l.next_due_date && new Date(l.next_due_date) < new Date()
              return (
                <Link
                  key={l.id}
                  href={`/backoffice/licenses/${l.id}`}
                  className={`block px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{l.display_name}</span>
                        <StatusBadge status={l.status} />
                        {l.issued && <Badge variant="outline" className="font-normal text-xs"><ShieldCheck className="w-3 h-3 mr-1 inline" />Issued</Badge>}
                        {l.expired && <Badge className="bg-red-100 text-red-700 font-normal text-xs"><ShieldAlert className="w-3 h-3 mr-1 inline" />Expired</Badge>}
                        {l.custom && <Badge variant="outline" className="font-normal text-xs"><Shield className="w-3 h-3 mr-1 inline" />Custom</Badge>}
                        {l.perpetual && <Badge variant="outline" className="font-normal text-xs">Perpetual</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {l.account_name} · {l.business_name}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isOverdue && l.status === 'active' && (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      )}
                      {l.latest_record_stage && <StageBadge stage={l.latest_record_stage} variant="license" />}
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
